import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Heart, MessageCircle, Send, Image as ImageIcon, Video, X, Trash2, Mic, Square, CheckCircle, Share2, Facebook, Twitter, Linkedin, Copy, Check, Edit2, ChevronLeft, ChevronRight, HelpCircle, Pin, PinOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import NotificationBell from '../components/NotificationBell';
import VoiceNotePlayer from '../components/VoiceNotePlayer';
import VideoUploadHelp from '../components/VideoUploadHelp';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Utility functions for link detection and embedding
const extractYouTubeId = (url) => {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

const extractTikTokId = (url) => {
  const pattern = /tiktok\.com\/@[\w.-]+\/video\/(\d+)/;
  const match = url.match(pattern);
  return match ? match[1] : null;
};

const extractInstagramId = (url) => {
  const patterns = [
    /instagram\.com\/p\/([a-zA-Z0-9_-]+)/,
    /instagram\.com\/reel\/([a-zA-Z0-9_-]+)/
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

const extractLinks = (text) => {
  if (!text) return [];
  const urlPattern = /(https?:\/\/[^\s]+)/g;
  return text.match(urlPattern) || [];
};

const LinkPreview = ({ url }) => {
  const youtubeId = extractYouTubeId(url);
  const tiktokId = extractTikTokId(url);
  const instagramId = extractInstagramId(url);
  
  // YouTube embed
  if (youtubeId) {
    return (
      <div className="mt-4 rounded-xl overflow-hidden shadow-lg bg-black">
        <iframe
          width="100%"
          height="315"
          src={`https://www.youtube.com/embed/${youtubeId}`}
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full"
        ></iframe>
      </div>
    );
  }
  
  // TikTok embed
  if (tiktokId) {
    return (
      <div className="mt-4 rounded-xl overflow-hidden shadow-lg">
        <blockquote 
          className="tiktok-embed" 
          cite={url}
          data-video-id={tiktokId}
          style={{ maxWidth: '605px', minWidth: '325px' }}
        >
          <section>
            <a 
              target="_blank" 
              rel="noopener noreferrer"
              href={url}
              className="block p-4 border-2 border-gray-200 rounded-xl hover:border-purple-400 hover:bg-purple-50"
            >
              <div className="flex items-center space-x-2">
                <span className="text-2xl">🎵</span>
                <div>
                  <p className="text-sm font-semibold text-purple-600">TikTok Video</p>
                  <p className="text-xs text-gray-500">Click to watch on TikTok</p>
                </div>
              </div>
            </a>
          </section>
        </blockquote>
      </div>
    );
  }
  
  // Instagram embed (simplified - Instagram requires complex embed code)
  if (instagramId) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-4 block p-4 border-2 border-pink-200 rounded-xl hover:border-pink-400 hover:bg-pink-50 transition-all"
      >
        <div className="flex items-center space-x-2">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 rounded-lg flex items-center justify-center">
            <span className="text-white text-xl">📷</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-pink-600">Instagram Post</p>
            <p className="text-xs text-gray-500 truncate">{url}</p>
          </div>
        </div>
      </a>
    );
  }
  
  // Twitter/X posts
  if (url.includes('twitter.com') || url.includes('x.com')) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-4 block p-4 border-2 border-blue-200 rounded-xl hover:border-blue-400 hover:bg-blue-50 transition-all"
      >
        <div className="flex items-center space-x-2">
          <div className="w-10 h-10 bg-blue-400 rounded-lg flex items-center justify-center">
            <span className="text-white text-xl">𝕏</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-blue-600">Twitter/X Post</p>
            <p className="text-xs text-gray-500 truncate">{url}</p>
          </div>
        </div>
      </a>
    );
  }
  
  // For other links, show a clickable preview
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="mt-4 block p-4 border-2 border-gray-200 rounded-xl hover:border-blue-400 hover:bg-blue-50 transition-all"
    >
      <div className="flex items-center space-x-2">
        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
          <span className="text-blue-600 text-xs font-bold">🔗</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-blue-600 truncate">{url}</p>
          <p className="text-xs text-gray-500">Click to open link</p>
        </div>
      </div>
    </a>
  );
};

// MediaCarousel Component for displaying multiple media items (Facebook-style)
const MediaCarousel = ({ mediaUrls, mediaType, backendUrl }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!mediaUrls || mediaUrls.length === 0) return null;

  const getFullUrl = (url) => {
    return url.startsWith('http') ? url : `${backendUrl}${url}`;
  };

  // Detect media type from URL extension and Cloudinary path
  const getMediaType = (url) => {
    const lowerUrl = url.toLowerCase();
    
    // Check for Cloudinary video URLs
    if (lowerUrl.includes('cloudinary.com') && lowerUrl.includes('/video/')) {
      return 'video';
    }
    
    // Check for Cloudinary image URLs
    if (lowerUrl.includes('cloudinary.com') && lowerUrl.includes('/image/')) {
      return 'image';
    }
    
    // Check for audio
    if (lowerUrl.includes('/audio/') || lowerUrl.match(/\.(mp3|wav|ogg|webm|m4a)(\?|$)/)) {
      return 'audio';
    }
    
    // Check for video file extensions
    if (lowerUrl.match(/\.(mp4|mov|avi|webm|mkv)(\?|$)/)) {
      return 'video';
    }
    
    // Default to image
    return 'image';
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? mediaUrls.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === mediaUrls.length - 1 ? 0 : prev + 1));
  };

  const currentMedia = mediaUrls[currentIndex];
  const currentMediaType = getMediaType(currentMedia);

  return (
    <div className="mb-4 space-y-2">
      {/* Main Media Display */}
      <div className="relative group bg-black rounded-xl overflow-hidden">
        {/* Counter Badge */}
        {mediaUrls.length > 1 && (
          <div className="absolute top-3 right-3 bg-black/70 text-white text-sm px-3 py-1 rounded-full z-10">
            {currentIndex + 1} / {mediaUrls.length}
          </div>
        )}

        {/* Media Content */}
        {currentMediaType === 'audio' ? (
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4">
            <VoiceNotePlayer 
              audioUrl={getFullUrl(currentMedia)}
              isMe={false}
            />
          </div>
        ) : currentMediaType === 'video' ? (
          <video 
            src={getFullUrl(currentMedia)}
            controls 
            className="w-full max-h-[500px] object-contain"
            playsInline
            preload="metadata"
            webkit-playsinline="true"
            x5-playsinline="true"
          >
            Your browser does not support video playback.
          </video>
        ) : (
          <img 
            src={getFullUrl(currentMedia)}
            alt={`Media ${currentIndex + 1}`}
            className="w-full max-h-[500px] object-contain cursor-pointer" 
            onClick={() => window.open(getFullUrl(currentMedia))}
          />
        )}

        {/* Navigation Arrows (only if multiple media) */}
        {mediaUrls.length > 1 && (
          <>
            <button
              onClick={goToPrevious}
              className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
              aria-label="Previous"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
              aria-label="Next"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}
      </div>

      {/* Thumbnail Strip (Facebook-style) - only show if multiple media */}
      {mediaUrls.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {mediaUrls.map((url, index) => {
            const thumbType = getMediaType(url);
            return (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`relative flex-shrink-0 rounded-lg overflow-hidden transition-all ${
                  index === currentIndex 
                    ? 'ring-4 ring-purple-500 opacity-100' 
                    : 'opacity-60 hover:opacity-100'
                }`}
              >
                {thumbType === 'video' ? (
                  <div className="relative w-20 h-20">
                    <video 
                      src={getFullUrl(url)}
                      className="w-full h-full object-cover"
                      muted
                      playsInline
                      preload="metadata"
                      crossOrigin="anonymous"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center pointer-events-none">
                      <Video className="w-6 h-6 text-white drop-shadow-lg" />
                    </div>
                  </div>
                ) : thumbType === 'audio' ? (
                  <div className="relative w-20 h-20 bg-gradient-to-r from-purple-100 to-pink-100 flex items-center justify-center">
                    <span className="text-2xl">🎵</span>
                  </div>
                ) : (
                  <img 
                    src={getFullUrl(url)}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-20 h-20 object-cover"
                  />
                )}
                {index === currentIndex && (
                  <div className="absolute inset-0 border-2 border-white rounded-lg" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const FeedPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPostText, setNewPostText] = useState('');
  const [newPostMedia, setNewPostMedia] = useState([]); // Array for multiple files
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [posting, setPosting] = useState(false);
  const [showComments, setShowComments] = useState({});
  const [comments, setComments] = useState({});
  const [newComment, setNewComment] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const fileInputRef = useRef(null);
  
  // Help modal state
  const [showVideoHelp, setShowVideoHelp] = useState(false);
  
  // Tab and category states
  const [activeTab, setActiveTab] = useState('single'); // 'single' or 'relationship'
  const [postCategory, setPostCategory] = useState('single');
  
  // Voice recording states
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);
  
  // Share states
  const [showShareMenu, setShowShareMenu] = useState({});
  const [copiedPostId, setCopiedPostId] = useState(null);
  
  // Upload progress states
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Edit post states
  const [editingPostId, setEditingPostId] = useState(null);
  const [editedPostText, setEditedPostText] = useState('');
  const [showEditDialog, setShowEditDialog] = useState(false);

  useEffect(() => {
    loadFeed(activeTab);
    loadCurrentUser();
    
    // Sync postCategory with activeTab
    setPostCategory(activeTab);
    
    // Set up real-time polling (every 10 seconds)
    const interval = setInterval(() => {
      loadFeed(activeTab);
    }, 10000);
    
    // Close share menu when clicking outside
    const handleClickOutside = (event) => {
      if (!event.target.closest('.share-menu-container')) {
        setShowShareMenu({});
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    
    return () => {
      clearInterval(interval);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [activeTab]);

  const loadCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setCurrentUser(response.data);
    } catch (e) {
      console.error('Failed to load user:', e);
    }
  };

  const loadFeed = async (category = null) => {
    try {
      const params = category ? { category } : {};
      console.log('Loading feed with category:', category, 'params:', params);
      const response = await axios.get(`${API}/feed`, { 
        params,
        withCredentials: true 
      });
      console.log('Feed loaded:', response.data.length, 'posts');
      setPosts(response.data);
      setLoading(false);
    } catch (e) {
      console.error('Failed to load feed:', e);
      toast.error('Failed to load feed');
      setLoading(false);
    }
  };

  const handleMediaUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    setUploadingMedia(true);
    setUploadProgress(0);
    
    try {
      const uploadedMedia = [];
      
      // Upload each file sequentially
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(`${API}/upload/post-media`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
          onUploadProgress: (progressEvent) => {
            // Calculate overall progress across all files
            const fileProgress = (progressEvent.loaded * 100) / progressEvent.total;
            const overallProgress = ((i * 100) + fileProgress) / files.length;
            setUploadProgress(Math.round(overallProgress));
          }
        });

        // Cloudinary returns full URLs, local storage returns relative paths
        const mediaUrl = response.data.url.startsWith('http') 
          ? response.data.url 
          : `${BACKEND_URL}${response.data.url}`;
        uploadedMedia.push({ url: mediaUrl, type: response.data.media_type });
      }

      setNewPostMedia(uploadedMedia);
      toast.success(`${files.length} file(s) uploaded!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to upload media');
    } finally {
      setUploadingMedia(false);
      setUploadProgress(0);
      event.target.value = '';
    }
  };

  // Voice recording functions
  const startRecording = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast.error('Voice recording is not supported in this browser');
        return;
      }

      if (window.location.protocol === 'http:' && window.location.hostname !== 'localhost') {
        toast.error('Voice recording requires a secure connection (HTTPS)');
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

      toast.success('Recording started');
    } catch (e) {
      console.error('Recording error:', e);
      
      if (e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError') {
        toast.error('Microphone access denied. Please enable microphone permissions in your browser settings');
      } else if (e.name === 'NotFoundError' || e.name === 'DevicesNotFoundError') {
        toast.error('No microphone found. Please connect a microphone and try again.');
      } else if (e.name === 'NotReadableError' || e.name === 'TrackStartError') {
        toast.error('Microphone is already in use by another application');
      } else {
        toast.error('Failed to start recording. Please check your microphone permissions.');
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    }
  };

  const cancelRecording = () => {
    stopRecording();
    setAudioBlob(null);
    setRecordingTime(0);
    audioChunksRef.current = [];
  };

  const uploadVoiceNote = async () => {
    if (!audioBlob) return;

    setUploadingAudio(true);
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'voice-note.webm');

      const uploadResponse = await axios.post(`${API}/upload/audio`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });

      const audioUrl = `${BACKEND_URL}${uploadResponse.data.url}`;
      setNewPostMedia([{ url: audioUrl, type: 'audio' }]);
      setAudioBlob(null);
      setRecordingTime(0);
      toast.success('Voice note ready to post!');
    } catch (e) {
      console.error('Upload voice note error:', e);
      toast.error('Failed to upload voice note');
    } finally {
      setUploadingAudio(false);
    }
  };

  const createPost = async () => {
    if (!newPostText.trim() && newPostMedia.length === 0) {
      toast.error('Please add text or media');
      return;
    }

    setPosting(true);
    try {
      const response = await axios.post(`${API}/posts`, {
        text: newPostText,
        media_urls: newPostMedia.map(m => m.url),
        media_type: newPostMedia[0]?.type || 'image',
        category: postCategory
      }, { withCredentials: true });

      console.log('Post created:', response.data);
      toast.success('Posted!');
      setNewPostText('');
      setNewPostMedia([]);
      
      // Reload feed to show new post
      await loadFeed(activeTab);
    } catch (e) {
      console.error('Failed to create post:', e);
      toast.error(e.response?.data?.detail || 'Failed to post');
    } finally {
      setPosting(false);
    }
  };

  const likePost = async (postId) => {
    try {
      const response = await axios.post(`${API}/posts/${postId}/like`, {}, { withCredentials: true });
      
      // Update post in state
      setPosts(posts.map(p => 
        p.id === postId 
          ? { ...p, likes: Array(response.data.likes).fill(''), liked_by_me: response.data.liked }
          : p
      ));
    } catch (e) {
      toast.error('Failed to like post');
    }
  };

  const toggleComments = async (postId) => {
    if (showComments[postId]) {
      setShowComments({ ...showComments, [postId]: false });
    } else {
      // Load comments
      try {
        const response = await axios.get(`${API}/posts/${postId}/comments`);
        setComments({ ...comments, [postId]: response.data });
        setShowComments({ ...showComments, [postId]: true });
      } catch (e) {
        toast.error('Failed to load comments');
      }
    }
  };

  const addComment = async (postId) => {
    const commentText = newComment[postId];
    if (!commentText?.trim()) return;

    try {
      await axios.post(`${API}/posts/${postId}/comments`, {
        text: commentText
      }, { withCredentials: true });

      setNewComment({ ...newComment, [postId]: '' });
      
      // Reload comments
      const response = await axios.get(`${API}/posts/${postId}/comments`);
      setComments({ ...comments, [postId]: response.data });
      
      // Update comment count
      setPosts(posts.map(p => 
        p.id === postId ? { ...p, comment_count: p.comment_count + 1 } : p
      ));
    } catch (e) {
      toast.error('Failed to add comment');
    }
  };


  const toggleShareMenu = (postId) => {
    setShowShareMenu(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }));
  };

  const getPostUrl = (postId) => {
    // Share link is only the base URL
    return 'http://ios.matrixshopping.com';
  };

  const shareToFacebook = (post) => {
    const url = getPostUrl(post.id);
    const text = post.text || 'Check out this post!';
    const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`;
    window.open(shareUrl, '_blank', 'width=600,height=400');
    setShowShareMenu(prev => ({ ...prev, [post.id]: false }));
  };

  const shareToTwitter = (post) => {
    const url = getPostUrl(post.id);
    const text = post.text || 'Check out this post!';
    const shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
    window.open(shareUrl, '_blank', 'width=600,height=400');
    setShowShareMenu(prev => ({ ...prev, [post.id]: false }));
  };

  const shareToWhatsApp = (post) => {
    const url = getPostUrl(post.id);
    const text = post.text || 'Check out this post!';
    const shareUrl = `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`;
    window.open(shareUrl, '_blank');
    setShowShareMenu(prev => ({ ...prev, [post.id]: false }));
  };

  const shareToLinkedIn = (post) => {
    const url = getPostUrl(post.id);
    const shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
    window.open(shareUrl, '_blank', 'width=600,height=400');
    setShowShareMenu(prev => ({ ...prev, [post.id]: false }));
  };

  const copyPostLink = (post) => {
    const url = getPostUrl(post.id);
    navigator.clipboard.writeText(url).then(() => {
      setCopiedPostId(post.id);
      toast.success('Link copied to clipboard!');
      setTimeout(() => {
        setCopiedPostId(null);
        setShowShareMenu(prev => ({ ...prev, [post.id]: false }));
      }, 2000);
    }).catch(() => {
      toast.error('Failed to copy link');
    });
  };

  const deletePost = async (postId) => {
    try {
      const confirmed = window.confirm('Are you sure you want to delete this post?');
      if (!confirmed) return;

      await axios.delete(`${API}/posts/${postId}`, { withCredentials: true });
      toast.success('Post deleted');
      setPosts(posts.filter(p => p.id !== postId));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete post');
    }
  };

  const pinPost = async (postId) => {
    try {
      const response = await axios.post(`${API}/posts/${postId}/pin`, {}, { withCredentials: true });
      toast.success(response.data.message);
      // Update local state
      setPosts(posts.map(p => 
        p.id === postId 
          ? { ...p, pinned: response.data.pinned, pinned_at: response.data.pinned ? new Date().toISOString() : null }
          : p
      ));
      // Reload feed to get proper order
      loadFeed();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to pin post');
    }
  };

  const openEditDialog = (post) => {
    setEditingPostId(post.id);
    setEditedPostText(post.text || '');
    setShowEditDialog(true);
  };

  const closeEditDialog = () => {
    setShowEditDialog(false);
    setEditingPostId(null);
    setEditedPostText('');
  };

  const saveEditedPost = async () => {
    if (!editedPostText.trim()) {
      toast.error('Post text cannot be empty');
      return;
    }

    try {
      const response = await axios.put(
        `${API}/posts/${editingPostId}`,
        { text: editedPostText },
        { withCredentials: true }
      );

      // Update the post in the local state
      setPosts(posts.map(post => 
        post.id === editingPostId 
          ? { ...post, text: response.data.text, edited_at: new Date().toISOString() }
          : post
      ));

      toast.success('Post updated successfully');
      closeEditDialog();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update post');
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-pink-400 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm shadow-sm sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              data-testid="back-btn"
              onClick={() => navigate('/discover')}
              variant="ghost"
              size="icon"
              className="hover:bg-pink-100 rounded-full"
            >
              <ArrowLeft className="w-5 h-5 text-gray-700" />
            </Button>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent">
              Community Feed
            </h1>
          </div>
          <NotificationBell />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        {/* Tabs */}
        <div className="flex space-x-2">
          <button
            onClick={() => setActiveTab('single')}
            className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all ${
              activeTab === 'single'
                ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Single
          </button>
          <button
            onClick={() => setActiveTab('relationship')}
            className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all ${
              activeTab === 'relationship'
                ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Relationship/Marriage
          </button>
        </div>
        {/* Create Post */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg">
          {/* Category Selector - Only show on Relationship tab */}
          {activeTab === 'relationship' && (
            <div className="mb-4 flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700">Post as:</span>
              <select
                value={postCategory}
                onChange={(e) => setPostCategory(e.target.value)}
                className="px-4 py-2 rounded-lg border-2 border-purple-200 focus:border-purple-400 focus:outline-none text-sm font-medium bg-white"
              >
                <option value="single">Single</option>
                <option value="relationship">Relationship/Marriage</option>
              </select>
            </div>
          )}
          
          <Textarea
            data-testid="new-post-input"
            value={newPostText}
            onChange={(e) => setNewPostText(e.target.value)}
            placeholder="What's on your mind?"
            className="min-h-[100px] mb-4 border-2 border-purple-200 focus:border-purple-400"
          />
          
          {newPostMedia.length > 0 && (
            <div className="mb-4 space-y-2">
              {/* Media previews grid */}
              <div className="grid grid-cols-2 gap-2">
                {newPostMedia.map((media, index) => (
                  <div key={index} className="relative group">
                    {media.type === 'audio' ? (
                      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 h-32 flex items-center justify-center">
                        <div className="text-center">
                          <div className="text-3xl mb-1">🎵</div>
                          <span className="text-xs text-gray-600">Voice Note {index + 1}</span>
                        </div>
                      </div>
                    ) : media.type === 'video' ? (
                      <div className="relative">
                        <video 
                          src={media.url}
                          className="w-full rounded-xl h-32 object-cover"
                          muted
                          playsInline
                          preload="metadata"
                          crossOrigin="anonymous"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-30 rounded-xl flex items-center justify-center pointer-events-none">
                          <div className="bg-white bg-opacity-95 rounded-full p-3 shadow-lg">
                            <Video className="w-8 h-8 text-purple-600" />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <img 
                        src={media.url} 
                        alt={`Upload ${index + 1}`} 
                        className="w-full rounded-xl h-32 object-cover" 
                      />
                    )}
                    <button
                      onClick={() => setNewPostMedia(newPostMedia.filter((_, i) => i !== index))}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500">{newPostMedia.length} file(s) selected</p>
            </div>
          )}
          
          {/* Voice recording UI */}
          {(isRecording || audioBlob) && (
            <div className="mb-4 p-4 bg-purple-50 rounded-xl border-2 border-purple-200">
              {isRecording ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                    <span className="text-sm font-medium text-gray-700">
                      Recording: {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
                    </span>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      onClick={cancelRecording}
                      variant="outline"
                      size="sm"
                      className="border-red-300 text-red-600 hover:bg-red-50"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Cancel
                    </Button>
                    <Button
                      onClick={stopRecording}
                      size="sm"
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      <Square className="w-4 h-4 mr-1" />
                      Stop
                    </Button>
                  </div>
                </div>
              ) : audioBlob ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Voice note ready</span>
                    <div className="flex space-x-2">
                      <Button
                        onClick={cancelRecording}
                        variant="outline"
                        size="sm"
                        className="border-red-300 text-red-600 hover:bg-red-50"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Delete
                      </Button>
                      <Button
                        onClick={uploadVoiceNote}
                        disabled={uploadingAudio}
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        {uploadingAudio ? 'Uploading...' : 'Use Voice Note'}
                      </Button>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          )}
          
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                multiple
                onChange={handleMediaUpload}
                className="hidden"
              />
              
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingMedia || newPostMedia.length > 0 || isRecording || audioBlob}
                variant="outline"
                className="flex-1"
              >
                <ImageIcon className="w-4 h-4 mr-2" />
                {uploadingMedia ? 'Uploading...' : 'Photo/Video'}
              </Button>
              
              <Button
                onClick={() => setShowVideoHelp(true)}
                variant="ghost"
                size="icon"
                className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                title="Video upload help"
              >
                <HelpCircle className="w-5 h-5" />
              </Button>
              
              <Button
                onClick={isRecording ? stopRecording : startRecording}
                disabled={newPostMedia.length > 0 || audioBlob || uploadingMedia}
                variant="outline"
                className={`flex-1 ${isRecording ? 'border-red-500 text-red-600' : ''}`}
              >
                <Mic className="w-4 h-4 mr-2" />
                {isRecording ? 'Recording...' : 'Voice Note'}
              </Button>
              
              <Button
                data-testid="post-btn"
                onClick={createPost}
                disabled={posting || (!newPostText.trim() && newPostMedia.length === 0)}
                className="flex-1 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white"
              >
                {posting ? 'Posting...' : 'Post'}
              </Button>
            </div>
            <p className="text-xs text-gray-500">Tip: Select multiple files to create a media carousel</p>
          </div>
          
          {/* Upload Progress Bar */}
          {uploadingMedia && (
            <div className="mt-4 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-700 font-medium">Uploading video...</span>
                <span className="text-purple-600 font-bold">{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-pink-500 to-purple-600 h-3 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 text-center">Please wait while your video is being uploaded</p>
            </div>
          )}
        </div>

        {/* Feed Posts */}
        {posts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No posts yet. Be the first to post!</p>
          </div>
        ) : (
          posts.map((post) => (
            <div key={post.id} className={`bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg ${post.pinned ? 'border-2 border-yellow-400 ring-2 ring-yellow-200' : ''}`}>
              {/* Pinned Indicator */}
              {post.pinned && (
                <div className="flex items-center space-x-2 mb-3 bg-yellow-50 px-3 py-2 rounded-lg border border-yellow-200">
                  <Pin className="w-4 h-4 text-yellow-600" />
                  <span className="text-sm font-semibold text-yellow-700">📌 Pinned Post</span>
                </div>
              )}
              {/* Post Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3 flex-1">
                  <Avatar
                    className="w-12 h-12 cursor-pointer"
                    onClick={() => navigate(`/profile/${post.user.id}`)}
                  >
                    <AvatarImage 
                      src={
                        post.user.photos?.[0] && !post.user.photos[0].includes('datematch-app')
                          ? (post.user.photos[0].startsWith('http') ? post.user.photos[0] : `${BACKEND_URL}${post.user.photos[0]}`)
                          : post.user.picture || ''
                      }
                      onError={(e) => {
                        // If image fails to load, try the Google picture as fallback
                        if (post.user.picture && e.target.src !== post.user.picture) {
                          e.target.src = post.user.picture;
                        }
                      }}
                    />
                    <AvatarFallback className="bg-gradient-to-br from-pink-400 to-purple-500 text-white">
                      {post.user.name[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h3
                        className="font-bold text-gray-800 cursor-pointer hover:text-pink-600"
                        onClick={() => navigate(`/profile/${post.user.id}`)}
                      >
                        {post.user.name}
                      </h3>
                      {post.user.verification_status === 'approved' && (
                        <div className="flex items-center space-x-1 bg-blue-100 px-2 py-0.5 rounded-full">
                          <CheckCircle className="w-3 h-3 text-blue-600" />
                          <span className="text-xs font-semibold text-blue-700">Verified</span>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">
                      {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
                      {post.edited_at && <span className="ml-2 text-xs text-gray-400">(edited)</span>}
                    </p>
                  </div>
                </div>
                {currentUser && (currentUser.id === post.user.id || currentUser.is_admin) && (
                  <div className="flex space-x-1 flex-shrink-0 ml-2">
                    {/* Pin button - Admin only */}
                    {currentUser.is_admin && (
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          pinPost(post.id);
                        }}
                        variant="ghost"
                        size="icon"
                        className={`${post.pinned ? 'text-yellow-600 hover:text-yellow-700 bg-yellow-50' : 'text-gray-500 hover:text-yellow-600'} hover:bg-yellow-50`}
                        title={post.pinned ? "Unpin post" : "Pin post"}
                        data-testid="pin-post-btn"
                      >
                        {post.pinned ? <PinOff className="w-5 h-5" /> : <Pin className="w-5 h-5" />}
                      </Button>
                    )}
                    {currentUser.id === post.user.id && (
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditDialog(post);
                        }}
                        variant="ghost"
                        size="icon"
                        className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 cursor-pointer"
                        title="Edit post"
                        data-testid="edit-post-btn"
                      >
                        <Edit2 className="w-5 h-5" />
                      </Button>
                    )}
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        deletePost(post.id);
                      }}
                      variant="ghost"
                      size="icon"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      title="Delete post"
                      data-testid="delete-post-btn"
                    >
                      <Trash2 className="w-5 h-5" />
                    </Button>
                  </div>
                )}
              </div>

              {/* Post Content */}
              {post.text && (
                <>
                  <p className="text-gray-700 mb-4 whitespace-pre-wrap">{post.text}</p>
                  {/* Render embedded content for links */}
                  {extractLinks(post.text).map((link, idx) => (
                    <LinkPreview key={idx} url={link} />
                  ))}
                </>
              )}
              
              {post.media_urls?.length > 0 && (
                <MediaCarousel 
                  mediaUrls={post.media_urls}
                  mediaType={post.media_type}
                  backendUrl={BACKEND_URL}
                />
              )}

              {/* Actions */}
              <div className="flex items-center space-x-6 pt-4 border-t border-gray-200">
                <button
                  onClick={() => likePost(post.id)}
                  className="flex items-center space-x-2 text-gray-600 hover:text-pink-500"
                  style={{transition: 'color 0.2s ease'}}
                >
                  <Heart className={`w-5 h-5 ${post.liked_by_me ? 'fill-pink-500 text-pink-500' : ''}`} />
                  <span>{post.likes?.length || 0}</span>
                </button>
                
                <button
                  onClick={() => toggleComments(post.id)}
                  className="flex items-center space-x-2 text-gray-600 hover:text-purple-500"
                  style={{transition: 'color 0.2s ease'}}
                >
                  <MessageCircle className="w-5 h-5" />
                  <span>{post.comment_count || 0}</span>
                </button>

                {/* Share Button */}
                <div className="relative share-menu-container">
                  <button
                    onClick={() => toggleShareMenu(post.id)}
                    className="flex items-center space-x-2 text-gray-600 hover:text-blue-500"
                    style={{transition: 'color 0.2s ease'}}
                  >
                    <Share2 className="w-5 h-5" />
                    <span>Share</span>
                  </button>

                  {/* Share Menu Dropdown */}
                  {showShareMenu[post.id] && (
                    <div className="absolute left-0 mt-2 w-56 bg-white rounded-lg shadow-2xl border border-gray-200 py-2 z-50">
                      <button
                        onClick={() => shareToFacebook(post)}
                        className="w-full flex items-center space-x-3 px-4 py-2 hover:bg-blue-50 text-gray-700 hover:text-blue-600"
                      >
                        <Facebook className="w-5 h-5" />
                        <span>Share to Facebook</span>
                      </button>
                      
                      <button
                        onClick={() => shareToTwitter(post)}
                        className="w-full flex items-center space-x-3 px-4 py-2 hover:bg-sky-50 text-gray-700 hover:text-sky-600"
                      >
                        <Twitter className="w-5 h-5" />
                        <span>Share to Twitter</span>
                      </button>
                      
                      <button
                        onClick={() => shareToWhatsApp(post)}
                        className="w-full flex items-center space-x-3 px-4 py-2 hover:bg-green-50 text-gray-700 hover:text-green-600"
                      >
                        <MessageCircle className="w-5 h-5" />
                        <span>Share to WhatsApp</span>
                      </button>
                      
                      <button
                        onClick={() => shareToLinkedIn(post)}
                        className="w-full flex items-center space-x-3 px-4 py-2 hover:bg-blue-50 text-gray-700 hover:text-blue-700"
                      >
                        <Linkedin className="w-5 h-5" />
                        <span>Share to LinkedIn</span>
                      </button>
                      
                      <div className="border-t border-gray-200 my-1"></div>
                      
                      <button
                        onClick={() => copyPostLink(post)}
                        className="w-full flex items-center space-x-3 px-4 py-2 hover:bg-gray-50 text-gray-700"
                      >
                        {copiedPostId === post.id ? (
                          <>
                            <Check className="w-5 h-5 text-green-600" />
                            <span className="text-green-600">Link Copied!</span>
                          </>
                        ) : (
                          <>
                            <Copy className="w-5 h-5" />
                            <span>Copy Link</span>
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Comments Section */}
              {showComments[post.id] && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  {/* Comments List */}
                  <div className="space-y-3 mb-4">
                    {comments[post.id]?.map((comment) => (
                      <div key={comment.id} className="flex space-x-2">
                        <Avatar className="w-8 h-8">
                          <AvatarImage src={comment.user.photos?.[0]?.startsWith('http') ? comment.user.photos[0] : `${BACKEND_URL}${comment.user.photos?.[0] || comment.user.picture || ''}`} />
                          <AvatarFallback>{comment.user.name[0]}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 bg-gray-100 rounded-xl p-3">
                          <div className="flex items-center space-x-2 mb-1">
                            <p className="font-semibold text-sm text-gray-800">{comment.user.name}</p>
                            {comment.user.verification_status === 'approved' && (
                              <div className="flex items-center space-x-1 bg-blue-100 px-1.5 py-0.5 rounded-full">
                                <CheckCircle className="w-2.5 h-2.5 text-blue-600" />
                                <span className="text-[10px] font-semibold text-blue-700">Verified</span>
                              </div>
                            )}
                          </div>
                          <p className="text-sm text-gray-700">{comment.text}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Add Comment */}
                  <div className="flex space-x-2">
                    <Input
                      value={newComment[post.id] || ''}
                      onChange={(e) => setNewComment({ ...newComment, [post.id]: e.target.value })}
                      placeholder="Write a comment..."
                      className="flex-1"
                      onKeyPress={(e) => e.key === 'Enter' && addComment(post.id)}
                    />
                    <Button
                      onClick={() => addComment(post.id)}
                      size="icon"
                      className="bg-purple-500 hover:bg-purple-600"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Edit Post Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Post</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              value={editedPostText}
              onChange={(e) => setEditedPostText(e.target.value)}
              placeholder="What's on your mind?"
              className="min-h-[100px] border-2 border-purple-200 focus:border-purple-400"
            />
            <div className="flex justify-end space-x-2">
              <Button
                onClick={closeEditDialog}
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                onClick={saveEditedPost}
                className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white"
              >
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Video Upload Help Modal */}
      {showVideoHelp && (
        <VideoUploadHelp onClose={() => setShowVideoHelp(false)} />
      )}
    </div>
  );
};

export default FeedPage;
